import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import useSextforceLiveStreamAutoCommentDelete, {
    liveStreamCommentsBlackList,
} from '../../../hooks/useSextforceLiveStreamAutoCommentDelete';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

const AutoCommentDeleteIntroCard = () => {
    return (
        <StyledCard>
            <Stack direction="column" spacing={2} alignItems="flex-start">
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Keep your live stream chat clean
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Add words or phrases below and any live stream comment that contains one of them will be automatically deleted.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
                    <Typography variant="body2" color="text.secondary">
                        Matching is case-insensitive — words are stored in lowercase.
                    </Typography>
                </Stack>
            </Stack>
        </StyledCard>
    );
};

const BlacklistedWordRow = ({
    index,
    word,
    isRemoving,
    removeDisabled,
    onRemove,
}: {
    index: number;
    word: liveStreamCommentsBlackList;
    isRemoving: boolean;
    removeDisabled: boolean;
    onRemove: () => void;
}) => {
    const theme: Theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px',
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {index}
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
                {word.blackListWord}
            </Typography>
            <IconButton aria-label="remove" color="error" size="small" disabled={removeDisabled} onClick={onRemove}>
                {isRemoving ? <CircularProgress color="inherit" size={20} /> : <DeleteOutlineIcon />}
            </IconButton>
        </Box>
    );
};

const SextforceLiveStreamAutoCommentDelete = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const inputRef = useRef<HTMLInputElement>(null);
    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();
    const {
        data: blackList,
        isLoading,
        refetch,
        addToCommentBlackList,
        removeFromCommentBlackList,
        getServiceStatus,
        updateServiceStatus,
        isLoadingCommentBlackList,
        isLoadingServiceStatus,
    } = useSextforceLiveStreamAutoCommentDelete();

    const [newWord, setNewWord] = useState<string>('');
    const [addLoading, setAddLoading] = useState<boolean>(false);
    const [removingWord, setRemovingWord] = useState<string | null>(null);
    const [clearAllLoading, setClearAllLoading] = useState<boolean>(false);
    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [blacklistExpanded, setBlacklistExpanded] = useState<boolean>(true);

    const words = useMemo(() => blackList ?? [], [blackList]);
    const sortedWords = useMemo(() => [...words].sort((a, b) => a.blackListWord.localeCompare(b.blackListWord)), [words]);
    const existingWords = useMemo(() => new Set(words.map(w => w.blackListWord)), [words]);

    const sextforceActive = useMemo(() => {
        if (subscriber && isSextforceActive(subscriber)) {
            return true;
        }
        return false;
    }, [subscriber]);

    useEffect(() => {
        if (!sextforceActive) {
            setServiceActive(false);
        } else {
            getServiceStatus()
                .then(active => setServiceActive(active))
                .catch(() => setServiceActive(false));
        }
    }, [sextforceActive]);

    const handleAdd = async () => {
        const onlyfansId = subscriber?.ofUserId;
        if (!onlyfansId || !sextforceActive) return;

        const normalized = newWord.trim().toLowerCase();
        if (!normalized) return;

        if (existingWords.has(normalized)) {
            enqueueSnackbar('That word is already on the list.', { variant: 'info', autoHideDuration: 3000 });
            return;
        }

        setAddLoading(true);
        const ok = await addToCommentBlackList({ onlyfansId, blackListWord: normalized });
        if (ok) {
            setNewWord('');
            await refetch();
            enqueueSnackbar(`"${normalized}" added to blacklist`, { variant: 'success', autoHideDuration: 3000 });
            inputRef.current?.focus();
        } else {
            enqueueSnackbar('Error trying to add word to the list.', { variant: 'error', autoHideDuration: 3000 });
        }
        setAddLoading(false);
    };

    const handleRemove = async (word: liveStreamCommentsBlackList) => {
        const onlyfansId = subscriber?.ofUserId;
        if (!onlyfansId) return;

        setRemovingWord(word.blackListWord);
        const ok = await removeFromCommentBlackList({ onlyfansId, blackListWord: word.blackListWord });
        if (ok) {
            await refetch();
            enqueueSnackbar(`"${word.blackListWord}" removed from blacklist`, { variant: 'success', autoHideDuration: 3000 });
        } else {
            enqueueSnackbar('Error trying to remove word from the list.', { variant: 'error', autoHideDuration: 3000 });
        }
        setRemovingWord(null);
    };

    const handleClearAll = () => {
        if (sortedWords.length === 0 || !sextforceActive) return;

        dialog
            .confirm({
                title: 'Clear all words?',
                message: `Are you sure you want to remove all ${sortedWords.length.toLocaleString()} word(s) from the blacklist?`,
                cancel: { text: 'Cancel', color: 'primary', variant: 'outlined' },
                ok: { text: 'Clear All', color: 'error', variant: 'contained' },
            })
            .then(async () => {
                const onlyfansId = subscriber?.ofUserId;
                if (!onlyfansId) return;

                setClearAllLoading(true);
                let allOk = true;
                for (const word of sortedWords) {
                    const ok = await removeFromCommentBlackList({ onlyfansId, blackListWord: word.blackListWord });
                    if (!ok) {
                        allOk = false;
                        break;
                    }
                }
                await refetch();
                if (allOk) {
                    enqueueSnackbar('All words removed from blacklist', { variant: 'success', autoHideDuration: 3000 });
                } else {
                    enqueueSnackbar('Error removing some words from the list.', { variant: 'error', autoHideDuration: 3000 });
                }
                setClearAllLoading(false);
            })
            .catch(() => {});
    };

    const confirmRemoveWord = (word: liveStreamCommentsBlackList) => {
        dialog
            .confirm({
                title: 'Remove word?',
                message: `Are you sure you want to remove "${word.blackListWord}" from the list?`,
                cancel: { text: 'Cancel', color: 'primary', variant: 'outlined' },
                ok: { text: 'Remove', color: 'error', variant: 'contained' },
            })
            .then(() => handleRemove(word))
            .catch(() => {});
    };

    const listControlsDisabled = !sextforceActive || addLoading || clearAllLoading || removingWord !== null;

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />
                        Auto Comment Delete for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Comment Delete" />
                    </StyledCard>

                    <AutoCommentDeleteIntroCard />

                    <StyledCard>
                        {isSubscriberLoading ? (
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                <Skeleton variant="text" width="70%" height={32} />
                                <Skeleton width={87} height={51} />
                            </Stack>
                        ) : (
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs>
                                    <Stack
                                        direction="row"
                                        spacing={1.25}
                                        alignItems="center"
                                        sx={{
                                            px: 1,
                                            py: 0.5,
                                            bgcolor: serviceActive
                                                ? alpha(theme.palette.success.main, 0.06)
                                                : alpha(theme.palette.error.main, 0.06),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                flexShrink: 0,
                                                bgcolor: serviceActive ? 'success.main' : 'error.main',
                                            }}
                                        />
                                        <div>
                                            Auto Comment Delete is currently{' '}
                                            {serviceActive ? (
                                                <span style={{ color: theme.palette.success.dark, fontWeight: 600 }}>running</span>
                                            ) : (
                                                <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>not running</span>
                                            )}
                                            .
                                        </div>
                                    </Stack>
                                </Grid>
                                <Grid item xs="auto">
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        disabled={!sextforceActive}
                                        onChange={e => {
                                            const newActiveStatus = e.target.checked;
                                            if (!sextforceActive || newActiveStatus === serviceActive) return;
                                            updateServiceStatus(newActiveStatus).then(success => {
                                                if (success) setServiceActive(newActiveStatus);
                                            });
                                        }}
                                        color="primary"
                                        loading={isLoadingServiceStatus || isLoadingCommentBlackList}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {!sextforceActive && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Sextforce service must be active to use this feature
                            </Alert>
                        )}
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <FormatListBulletedIcon sx={{ color: 'white', fontSize: 20 }} />
                                    <Typography variant="subtitle1" color="white">
                                        Blacklisted Words
                                    </Typography>
                                </Stack>
                            }
                            badge={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip
                                        label={words.length}
                                        size="small"
                                        sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 600, minWidth: 28 }}
                                    />
                                    <IconButton
                                        size="small"
                                        sx={{ color: 'white' }}
                                        aria-label={blacklistExpanded ? 'Collapse blacklist' : 'Expand blacklist'}
                                        onClick={() => setBlacklistExpanded(prev => !prev)}
                                    >
                                        {blacklistExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Stack>
                            }
                        />
                    </StyledCard>

                    <Collapse in={blacklistExpanded}>
                        <StyledCard>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add a word or phrase"
                                    value={newWord}
                                    inputRef={inputRef}
                                    onChange={e => setNewWord(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAdd();
                                        }
                                    }}
                                    disabled={listControlsDisabled}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    loading={addLoading}
                                    disabled={newWord.trim().length === 0 || listControlsDisabled}
                                    onClick={handleAdd}
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    <span>Add</span>
                                </LoadingButton>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, mb: 2 }}>
                                Press Enter or tap Add to save.
                            </Typography>

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {words.length.toLocaleString()} word{words.length === 1 ? '' : 's'} in list
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    disabled={words.length === 0 || listControlsDisabled}
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </Button>
                            </Stack>

                            {isLoading ? (
                                <Stack alignItems="center" py={3}>
                                    <CircularProgress size={28} />
                                </Stack>
                            ) : sortedWords.length === 0 ? (
                                <Stack alignItems="center" py={3} spacing={0.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        No words in the list yet.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Add your first word above.
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {sortedWords.map((word, index) => (
                                        <BlacklistedWordRow
                                            key={word.blackListWord}
                                            index={index + 1}
                                            word={word}
                                            isRemoving={removingWord === word.blackListWord}
                                            removeDisabled={listControlsDisabled}
                                            onRemove={() => confirmRemoveWord(word)}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </StyledCard>
                    </Collapse>
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamAutoCommentDelete;
